import { useState } from 'react';
import {
  Receipt, Users, CheckCircle, AlertTriangle, ArrowLeft, ArrowRight,
  Send, FileText, Loader2, Settings,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import { useBulkInvoicePreview, useBulkCreateInvoices } from '@/hooks/use-invoices';
import { useTerms, type Term } from '@/hooks/use-terms';
import { useSessionRates } from '@/hooks/use-session-rates';
import { formatCurrency } from '@/lib/format';
import { useNavigate } from 'react-router-dom';

interface BulkInvoiceWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

type Step = 'configure' | 'preview' | 'result';

export function BulkInvoiceWizard({ open, onOpenChange }: BulkInvoiceWizardProps) {
  const navigate = useNavigate();
  const [step, setStep] = useState<Step>('configure');

  // Configure form state
  const [termId, setTermId] = useState('');
  const [dueDate, setDueDate] = useState('');
  const [description, setDescription] = useState('');
  const [sendImmediately, setSendImmediately] = useState(false);
  const [skipUnpaid, setSkipUnpaid] = useState(true);

  // Preview state
  const [deselected, setDeselected] = useState<Set<string>>(new Set());

  // Result state
  const [result, setResult] = useState<{ created: number; skipped: number; totalAmountCents: number } | null>(null);

  const { data: terms, isLoading: termsLoading } = useTerms();
  const { data: sessionRates } = useSessionRates();
  const hasRatesConfigured = (sessionRates?.length ?? 0) > 0;

  const { data: previewData, isLoading: previewLoading } = useBulkInvoicePreview(
    step === 'preview' && termId
      ? { termId, skipUnpaid }
      : 'skip'
  );

  const bulkCreate = useBulkCreateInvoices();

  const selectedTerm = terms?.find((t: Term) => t.id === termId);
  const isConfigValid = termId.length > 0 && dueDate.length > 0;

  const eligibleParents = (previewData ?? []).filter(
    (p) => p && !p.willSkip && !deselected.has(p.parentUserId as string)
  );
  const skippedParents = (previewData ?? []).filter((p) => p && p.willSkip);

  const totalAmountCents = eligibleParents.reduce(
    (sum, p) => sum + (p?.totalAmountCents ?? 0),
    0
  );

  const handleCreate = async () => {
    const parentUserIds = eligibleParents.map((p) => p!.parentUserId as string);
    try {
      const res = await bulkCreate.mutateAsync({
        termId,
        dueDate,
        description: description || selectedTerm?.name,
        status: sendImmediately ? 'sent' : 'draft',
        parentUserIds,
        skipUnpaid,
      });
      setResult({ created: res.created, skipped: res.skipped, totalAmountCents });
      setStep('result');
    } catch {
      // error handled by mutation
    }
  };

  const handleClose = () => {
    onOpenChange(false);
    setTimeout(() => {
      setStep('configure');
      setTermId('');
      setDueDate('');
      setDescription('');
      setSendImmediately(false);
      setSkipUnpaid(true);
      setDeselected(new Set());
      setResult(null);
    }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Receipt className="w-5 h-5 text-emerald-600" />
            Generate Term Invoices
          </DialogTitle>
        </DialogHeader>

        {/* Step indicators */}
        <div className="flex items-center gap-2 mb-4">
          {(['configure', 'preview', 'result'] as Step[]).map((s, i) => (
            <div key={s} className="flex items-center gap-2">
              <div
                className={cn(
                  'w-8 h-8 rounded-full flex items-center justify-center text-sm font-medium',
                  step === s
                    ? 'bg-emerald-600 text-white'
                    : i < ['configure', 'preview', 'result'].indexOf(step)
                    ? 'bg-emerald-100 text-emerald-700'
                    : 'bg-gray-100 text-gray-400'
                )}
              >
                {i + 1}
              </div>
              {i < 2 && <div className="w-8 h-px bg-gray-200" />}
            </div>
          ))}
          <span className="text-sm text-gray-500 ml-2">
            {step === 'configure' && 'Configure'}
            {step === 'preview' && 'Preview'}
            {step === 'result' && 'Done'}
          </span>
        </div>

        {/* Step 1: Configure */}
        {step === 'configure' && (
          <div className="space-y-4">
            {/* No rates warning */}
            {!hasRatesConfigured && (
              <div className="flex items-start gap-2 p-3 rounded-md bg-amber-50 border border-amber-200 text-sm">
                <AlertTriangle className="w-4 h-4 text-amber-500 mt-0.5 flex-shrink-0" />
                <span className="text-amber-700">
                  No session rates configured.{' '}
                  <button
                    type="button"
                    onClick={() => { handleClose(); navigate('/director/settings'); }}
                    className="underline font-medium"
                  >
                    Set rates in Settings
                  </button>{' '}
                  before generating invoices.
                </span>
              </div>
            )}

            {/* Term selector */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Term
              </label>
              {termsLoading ? (
                <p className="text-sm text-muted-foreground">Loading terms...</p>
              ) : !terms || terms.length === 0 ? (
                <div className="flex items-center gap-2 p-3 rounded-md bg-gray-50 border border-gray-200 text-sm text-gray-600">
                  <Settings className="w-4 h-4 text-gray-400" />
                  No terms found.{' '}
                  <button
                    type="button"
                    onClick={() => { handleClose(); navigate('/director/settings'); }}
                    className="underline font-medium text-gray-700"
                  >
                    Create a term in Settings
                  </button>
                </div>
              ) : (
                <select
                  value={termId}
                  onChange={(e) => setTermId(e.target.value)}
                  className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white"
                >
                  <option value="">Select a term…</option>
                  {terms.map((t: Term) => (
                    <option key={t.id} value={t.id}>
                      {t.name} ({t.startDate} – {t.endDate})
                    </option>
                  ))}
                </select>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Due Date
              </label>
              <input
                type="date"
                value={dueDate}
                onChange={(e) => setDueDate(e.target.value)}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description <span className="text-gray-400 font-normal">(optional — defaults to term name)</span>
              </label>
              <input
                type="text"
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder={selectedTerm?.name ?? 'e.g. Term 2 2026 Fees'}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm"
              />
            </div>

            <div className="space-y-2">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={skipUnpaid}
                  onChange={(e) => setSkipUnpaid(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Skip parents with existing unpaid invoices
              </label>
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  checked={sendImmediately}
                  onChange={(e) => setSendImmediately(e.target.checked)}
                  className="rounded border-gray-300"
                />
                Send invoices immediately (instead of saving as draft)
              </label>
            </div>

            <div className="flex justify-end pt-2">
              <Button
                onClick={() => setStep('preview')}
                disabled={!isConfigValid}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Preview
                <ArrowRight className="w-4 h-4 ml-1" />
              </Button>
            </div>
          </div>
        )}

        {/* Step 2: Preview */}
        {step === 'preview' && (
          <div className="space-y-4">
            {/* Summary bar */}
            <Card>
              <CardContent className="p-3 flex items-center justify-between flex-wrap gap-2">
                <div className="text-sm">
                  <span className="font-medium">{eligibleParents.length}</span> invoices
                  {selectedTerm && (
                    <span className="text-gray-500 ml-1">for {selectedTerm.name}</span>
                  )}
                  {' '}= <span className="font-bold text-emerald-700">
                    {formatCurrency(totalAmountCents)}
                  </span>
                </div>
                <Badge variant="outline" className={sendImmediately ? 'border-blue-300 text-blue-700' : ''}>
                  {sendImmediately ? (
                    <><Send className="w-3 h-3 mr-1" /> Send Immediately</>
                  ) : (
                    <><FileText className="w-3 h-3 mr-1" /> Save as Draft</>
                  )}
                </Badge>
              </CardContent>
            </Card>

            {previewLoading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-6 h-6 animate-spin text-gray-400" />
              </div>
            ) : (previewData ?? []).length === 0 ? (
              <div className="text-center py-8 text-sm text-gray-500">
                <Users className="w-8 h-8 mx-auto mb-2 text-gray-300" />
                No parents have enrolled players for this term.
              </div>
            ) : (
              <>
                <div className="max-h-[340px] overflow-y-auto space-y-2">
                  {(previewData ?? []).map((parent) => {
                    if (!parent) return null;
                    const parentId = parent.parentUserId as string;
                    const isDeselected = deselected.has(parentId);
                    const willSkip = parent.willSkip;

                    return (
                      <div
                        key={parentId}
                        className={cn(
                          'rounded-lg border text-sm overflow-hidden',
                          willSkip
                            ? 'border-yellow-200 bg-yellow-50 opacity-60'
                            : isDeselected
                            ? 'border-gray-200 bg-gray-50 opacity-50'
                            : parent.hasMissingRates
                            ? 'border-amber-200 bg-white'
                            : 'border-gray-200 bg-white'
                        )}
                      >
                        {/* Parent header row */}
                        <div className="flex items-center gap-3 px-3 py-2.5">
                          {!willSkip && (
                            <input
                              type="checkbox"
                              checked={!isDeselected}
                              onChange={(e) => {
                                const next = new Set(deselected);
                                if (e.target.checked) {
                                  next.delete(parentId);
                                } else {
                                  next.add(parentId);
                                }
                                setDeselected(next);
                              }}
                              className="rounded border-gray-300 flex-shrink-0"
                            />
                          )}
                          {willSkip && (
                            <AlertTriangle className="w-4 h-4 text-yellow-500 flex-shrink-0" />
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="font-medium text-gray-800 truncate">
                              {parent.parentName}
                            </div>
                          </div>
                          {willSkip ? (
                            <Badge variant="outline" className="text-yellow-600 border-yellow-300 text-xs flex-shrink-0">
                              Has unpaid
                            </Badge>
                          ) : (
                            <span className="font-bold text-gray-900 flex-shrink-0">
                              {formatCurrency(parent.totalAmountCents)}
                            </span>
                          )}
                        </div>

                        {/* Per-child breakdown */}
                        {!willSkip && !isDeselected && (
                          <div className="border-t border-gray-100 px-3 py-2 space-y-1.5 bg-gray-50">
                            {parent.children.map((child) => (
                              <div key={child.playerId as string}>
                                <div className="text-xs font-medium text-gray-600 mb-0.5">
                                  {child.playerName}
                                </div>
                                {child.lineItems.map((li, i) => (
                                  <div key={i} className="flex justify-between text-xs text-gray-500 pl-2">
                                    <span>
                                      {li.description.replace(`${child.playerName} — `, '')}
                                    </span>
                                    <span>{formatCurrency(li.unitPrice)}</span>
                                  </div>
                                ))}
                                {child.missingRateTypes.length > 0 && (
                                  <div className="flex items-center gap-1 text-xs text-amber-600 pl-2 mt-0.5">
                                    <AlertTriangle className="w-3 h-3" />
                                    No rate for: {child.missingRateTypes.join(', ')}
                                  </div>
                                )}
                                {child.sessionTypes.length === 0 && (
                                  <div className="text-xs text-gray-400 pl-2">
                                    No enrollments for this term
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {skippedParents.length > 0 && (
                  <p className="text-xs text-yellow-600">
                    {skippedParents.length} parent(s) skipped due to existing unpaid invoices
                  </p>
                )}
              </>
            )}

            <div className="flex justify-between pt-2">
              <Button variant="outline" onClick={() => setStep('configure')}>
                <ArrowLeft className="w-4 h-4 mr-1" />
                Back
              </Button>
              <Button
                onClick={handleCreate}
                disabled={eligibleParents.length === 0 || bulkCreate.isPending}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                {bulkCreate.isPending ? (
                  <Loader2 className="w-4 h-4 mr-1 animate-spin" />
                ) : (
                  <CheckCircle className="w-4 h-4 mr-1" />
                )}
                Create {eligibleParents.length} Invoice{eligibleParents.length !== 1 ? 's' : ''}
              </Button>
            </div>
          </div>
        )}

        {/* Step 3: Result */}
        {step === 'result' && result && (
          <div className="text-center py-8 space-y-4">
            <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto">
              <CheckCircle className="w-8 h-8 text-emerald-600" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-gray-900">
                {result.created} Invoice{result.created !== 1 ? 's' : ''} Created
              </h3>
              {result.skipped > 0 && (
                <p className="text-sm text-yellow-600 mt-1">
                  {result.skipped} skipped (unpaid invoices, no enrollments, or missing rates)
                </p>
              )}
              <p className="text-sm text-gray-500 mt-2">
                Total: {formatCurrency(result.totalAmountCents)}
                {sendImmediately ? ' — Sent to parents' : ' — Saved as drafts'}
              </p>
            </div>
            <Button onClick={handleClose} className="bg-emerald-600 hover:bg-emerald-700">
              Done
            </Button>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
